import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Anchor, CheckCircle, XCircle, Loader } from "lucide-react";

const MAX_POLLS = 10;
const POLL_INTERVAL = 1500;

export default function BillingReturn() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const nav = useNavigate();
    const { refreshUser } = useAuth();
    const [state, setState] = useState({ status: "polling", attempt: 0, plan: null });
    const stopped = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "error", message: "No session id provided" });
            return;
        }
        let cancelled = false;

        const poll = async (attempt) => {
            if (cancelled || stopped.current) return;
            try {
                const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
                if (data.payment_status === "paid") {
                    stopped.current = true;
                    setState({ status: "paid", plan: data.plan });
                    // Refresh user in background — does NOT toggle global `loading`,
                    // so ProtectedRoute won't unmount this page mid-success.
                    refreshUser();
                    return;
                }
                if (data.status === "expired") {
                    setState({ status: "expired" });
                    stopped.current = true;
                    return;
                }
                if (attempt + 1 >= MAX_POLLS) {
                    setState({ status: "pending", plan: data.plan });
                    return;
                }
                setState((s) => ({ ...s, attempt: attempt + 1 }));
                setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
            } catch (e) {
                setState({ status: "error", message: e.response?.data?.detail || e.message });
            }
        };
        poll(0);
        return () => { cancelled = true; };
    }, [sessionId, refreshUser]);

    const planLabel = state.plan === "monthly" ? "Monthly"
        : state.plan === "annual" ? "Annual"
        : state.plan === "lifetime" ? "Lifetime"
        : "subscription";

    return (
        <div data-testid="billing-return-page" style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
            <div className="bm-card" style={{ maxWidth: 480, padding: 36, textAlign: "center" }}>
                <Link to="/app" style={{ display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none", marginBottom: 20 }}>
                    <Anchor size={18} color="var(--bm-gold)" />
                    <span className="bm-display" style={{ fontSize: 22 }}>Bosun's Mate</span>
                </Link>

                {state.status === "polling" && (
                    <>
                        <Loader size={36} color="var(--bm-blue)" className="bm-pulse" />
                        <h1 className="bm-serif" style={{ fontSize: 26, margin: "16px 0 6px", fontWeight: 500 }}>Confirming your payment…</h1>
                        <p style={{ color: "var(--bm-text-muted)", fontSize: 14, margin: 0 }}>This takes a few seconds. Don't close the tab.</p>
                    </>
                )}

                {state.status === "paid" && (
                    <>
                        <CheckCircle size={48} color="var(--bm-good)" />
                        <h1 className="bm-serif" style={{ fontSize: 28, margin: "16px 0 6px", fontWeight: 500 }}>Welcome aboard, captain.</h1>
                        <p style={{ color: "var(--bm-text-muted)", fontSize: 15, margin: "0 0 24px" }}>
                            Your {planLabel} plan is active. {state.plan === "lifetime" ? "You own Bosun's Mate for life ⚓" : "We'll see you on the water."}
                        </p>
                        <button data-testid="billing-return-continue-btn" className="bm-btn bm-btn-gold" onClick={() => nav("/app", { replace: true })} style={{ padding: "12px 22px" }}>
                            Take me to the helm
                        </button>
                    </>
                )}

                {state.status === "pending" && (
                    <>
                        <Loader size={36} color="var(--bm-warn)" />
                        <h1 className="bm-serif" style={{ fontSize: 24, margin: "16px 0 6px", fontWeight: 500 }}>Still processing…</h1>
                        <p style={{ color: "var(--bm-text-muted)", fontSize: 14, margin: "0 0 16px" }}>
                            Your payment is on its way. We'll email you when it clears. You can use the app while you wait.
                        </p>
                        <button className="bm-btn bm-btn-ghost" onClick={() => nav("/app", { replace: true })}>
                            Continue to app
                        </button>
                    </>
                )}

                {state.status === "expired" && (
                    <>
                        <XCircle size={40} color="var(--bm-bad)" />
                        <h1 className="bm-serif" style={{ fontSize: 24, margin: "16px 0 6px", fontWeight: 500 }}>Session expired</h1>
                        <p style={{ color: "var(--bm-text-muted)", fontSize: 14, margin: "0 0 18px" }}>The checkout took too long. No charge made.</p>
                        <button className="bm-btn bm-btn-primary" onClick={() => nav("/pricing")}>Try again</button>
                    </>
                )}

                {state.status === "error" && (
                    <>
                        <XCircle size={40} color="var(--bm-bad)" />
                        <h1 className="bm-serif" style={{ fontSize: 22, margin: "16px 0 6px", fontWeight: 500 }}>Something went sideways</h1>
                        <p style={{ color: "var(--bm-text-muted)", fontSize: 14, margin: "0 0 18px" }}>{state.message || "We couldn't confirm your payment."}</p>
                        <button className="bm-btn bm-btn-ghost" onClick={() => nav("/pricing")}>Back to pricing</button>
                    </>
                )}
            </div>
        </div>
    );
}
